import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { ArrowLeft, ExternalLink, Newspaper, Globe, RefreshCcw, Loader2, Languages, Bookmark, VolumeX, Volume2, Headphones, Pause, Play, Square, Clock, PlayCircle } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { useNewsArticle, useSavedArticle, useMarkArticleRead, useMutedSources, useSimilarArticles } from '../../hooks/useNews'
import RedditCommentsList from './components/RedditCommentsList'
import { GalleryCarousel, VideoBlock } from './components/RedditMedia'
import ArticleChat from './components/ArticleChat'
import ImageLightbox from '../../components/ImageLightbox'
import { useTextToSpeech } from '../../hooks/useTextToSpeech'
import { useArticleScrollPosition } from '../../hooks/useArticleScrollPosition'
import { stripMarkdownForTts } from '../../lib/stripMarkdown'
import { cn } from '../../lib/utils'
import { useArticleContent } from '../../hooks/useNewsContent'
import { useArticleTranslation } from '../../hooks/useNewsTranslation'
import { useTextTranslation } from '../../hooks/useTextTranslation'
import EnglishArticleDrill from '../../components/english/EnglishArticleDrill'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'

// YouTube URL patterns we accept and the rkey extraction. Supports
// the long form (?v=ID) and youtu.be short form. Returns null if the
// URL isn't a recognisable YouTube link — caller renders the post
// without an embed in that case.
function extractYouTubeId(url) {
  if (!url) return null
  try {
    const u = new URL(url)
    if (/(^|\.)youtu\.be$/i.test(u.hostname)) {
      return u.pathname.replace(/^\//, '') || null
    }
    if (/(^|\.)youtube\.com$/i.test(u.hostname)) {
      const v = u.searchParams.get('v')
      if (v) return v
      // /shorts/ID and /embed/ID variants.
      const m = u.pathname.match(/^\/(?:shorts|embed)\/([^/?]+)/)
      if (m) return m[1]
    }
  } catch { /* fall through */ }
  return null
}

// Detect whether a string is predominantly Arabic-script. Used to
// override the per-source language when an Arabic-script title shows
// up on an English-tagged source (e.g. r/saudiarabia mixes both).
// Returns true if > 30% of non-whitespace characters are in the
// Arabic Unicode block.
function isArabicText(s) {
  if (!s) return false
  let arabic = 0
  let total = 0
  for (const ch of s) {
    if (/\s/.test(ch)) continue
    total++
    if (/[؀-ۿ]/.test(ch)) arabic++
  }
  if (total === 0) return false
  return arabic / total > 0.3
}

// Quick reading-time estimate. Counts whitespace-separated tokens
// across the speakable text. 200 wpm is roughly average; we floor at
// 1 minute so very short posts don't show "0 min read".
function estimateReadingMinutes(text) {
  if (!text) return 0
  const words = text.trim().split(/\s+/).filter(Boolean).length
  if (words === 0) return 0
  return Math.max(1, Math.ceil(words / 200))
}

// Recognise YouTube/Vimeo/Bluesky video URLs in the markdown body so
// we can render a clickable preview card instead of a plain link.
function detectVideoEmbed(url) {
  if (!url) return null
  try {
    const u = new URL(url)
    if (/(^|\.)youtube\.com$/i.test(u.hostname) || /(^|\.)youtu\.be$/i.test(u.hostname)) {
      return { kind: 'youtube', id: extractYouTubeId(url) }
    }
    if (/(^|\.)vimeo\.com$/i.test(u.hostname)) {
      const id = u.pathname.match(/^\/(\d+)/)?.[1]
      return id ? { kind: 'vimeo', id } : null
    }
  } catch { /* not a URL */ }
  return null
}

// Inline tag chip for the article detail header. Mirrors the chip
// the listing card uses but with slightly larger padding to read
// well under the article title. Visual vocab matches TAG_STYLES in
// NewsPage so the same tag looks the same in both places.
const DETAIL_TAG_STYLES = {
  breaking:     { emoji: '🚨', tone: 'text-red-500 bg-red-500/10 border-red-500/30' },
  analysis:     { emoji: '📊', tone: 'text-blue-500 bg-blue-500/10 border-blue-500/30' },
  interview:    { emoji: '🎙', tone: 'text-purple-500 bg-purple-500/10 border-purple-500/30' },
  listicle:     { emoji: '📋', tone: 'text-amber-500 bg-amber-500/10 border-amber-500/30' },
  review:       { emoji: '⭐️', tone: 'text-amber-500 bg-amber-500/10 border-amber-500/30' },
  announcement: { emoji: '📣', tone: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30' },
  video:        { emoji: '▶️',  tone: 'text-app-primary-500 bg-app-primary-500/10 border-app-primary-500/30' },
  social:       { emoji: '💬', tone: 'text-app-primary-500 bg-app-primary-500/10 border-app-primary-500/30' },
  science:      { emoji: '🔬', tone: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/30' },
  sports:       { emoji: '⚽️', tone: 'text-green-500 bg-green-500/10 border-green-500/30' },
  politics:     { emoji: '🏛',  tone: 'text-rose-500 bg-rose-500/10 border-rose-500/30' },
  finance:      { emoji: '💰', tone: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30' },
  tech:         { emoji: '💻', tone: 'text-app-primary-500 bg-app-primary-500/10 border-app-primary-500/30' },
}

// Memoised article body. Pulled out of the parent so unrelated
// state changes (digest hooks, similar-articles arriving, etc.)
// don't force ReactMarkdown to re-parse the markdown — that
// re-parse was unmounting + remounting every <img> inside the body
// on each pass, which caused images to flash empty during scroll.
//
// React.memo gates the re-render on `markdown` + `dir`. Both come
// from useState/useMemo upstream so identity stays stable across
// scroll-driven re-renders. The components object is module-level
// so its identity never changes either.
const ARTICLE_MARKDOWN_COMPONENTS = {
  a: ({ node, href, children, ...props }) => {
    const embed = detectVideoEmbed(href)
    const isVideo = !!embed
    return (
      <a
        {...props}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={isVideo ? 'inline-flex items-center gap-1' : undefined}
      >
        {isVideo && <PlayCircle className="w-4 h-4 flex-shrink-0" />}
        <span>{children}</span>
      </a>
    )
  },
  img: ({ node, src, alt, ...props }) => (
    <img
      {...props}
      src={src}
      alt={alt || ''}
      loading="lazy"
      decoding="async"
      onError={(e) => { e.currentTarget.style.display = 'none' }}
      className="w-full h-auto rounded-lg my-4 bg-muted"
    />
  ),
}

const ArticleBody = React.memo(function ArticleBody({ markdown, dir }) {
  return (
    <div
      className={cn(
        'prose prose-sm sm:prose-base dark:prose-invert max-w-none text-foreground leading-relaxed',
        'prose-img:rounded-lg prose-img:my-4 prose-img:mx-auto',
        'prose-headings:font-bold prose-h2:text-xl prose-h3:text-lg',
        'prose-a:text-app-primary-500 prose-a:no-underline hover:prose-a:underline',
        'prose-blockquote:border-app-primary-500/40',
        // Long unbroken tokens — usually tracking-loaded URLs like
        // `?utm_source=...&utm_campaign=...` — were blowing the body
        // wider than the overlay's viewport, shifting the article off
        // the visible area on RTL. Force-wrap long words at any point.
        'break-words [overflow-wrap:anywhere]',
      )}
      dir={dir}
    >
      <ReactMarkdown components={ARTICLE_MARKDOWN_COMPONENTS}>
        {markdown}
      </ReactMarkdown>
    </div>
  )
})

function DetailTagChip({ tag }) {
  const { t } = useTranslation()
  const style = DETAIL_TAG_STYLES[tag] || { emoji: '', tone: 'text-muted-foreground bg-muted/40 border-border' }
  const label = t(`news.tag.${tag}`, { defaultValue: tag })
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full border',
        style.tone,
      )}
      dir="auto"
    >
      {style.emoji && <span aria-hidden="true">{style.emoji}</span>}
      <span>{label}</span>
    </span>
  )
}

// AI summary card. Source language is the article's own — when it
// differs from the UI locale, we offer a one-tap MS-Translator pass
// (and auto-fire it the first time so the user lands on translated
// content if their reading language differs). Extracted into its own
// component so the translation hooks live here instead of bloating
// NewsArticlePage's hook count.
function AiSummaryCard({ summary, articleLang, locale, t }) {
  const translator = useTextTranslation()
  const { translate } = translator
  const [showTranslated, setShowTranslated] = useState(false)
  const translateNeeded = !!articleLang && articleLang !== locale

  // `translate` is wrapped in useCallback with empty deps inside the
  // hook so its identity is stable across renders. The `alive` flag
  // discards a stale response if the inputs change mid-flight.
  useEffect(() => {
    if (!translateNeeded) return undefined
    let alive = true
    translate(summary, locale).then((out) => {
      if (alive && out) setShowTranslated(true)
    })
    return () => { alive = false }
  }, [summary, locale, translateNeeded, translate])

  const displayed = showTranslated && translator.translation
    ? translator.translation
    : summary
  const displayedDir = showTranslated && translator.translation
    ? (locale === 'ar' ? 'rtl' : 'ltr')
    : (isArabicText(summary) ? 'rtl' : 'ltr')

  const handleTranslate = async () => {
    if (translator.loading) return
    if (translator.translation) {
      setShowTranslated((v) => !v)
      return
    }
    // Target = opposite of source. Detect the source from articleLang
    // (preferred) or fall back to the summary's actual script — the
    // article's `language` column isn't always accurate for mixed-
    // language content.
    const detectedSource = articleLang === 'ar' || isArabicText(summary) ? 'ar' : 'en'
    const target = detectedSource === 'ar' ? 'en' : 'ar'
    const out = await translator.translate(summary, target)
    if (out) setShowTranslated(true)
  }

  return (
    <Card className="border-app-primary-500/30 bg-app-primary-500/5">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <p className="text-[11px] text-app-primary-500 font-semibold uppercase tracking-wide flex-1 min-w-0" dir="auto">
            {t('news.article.aiSummary')}
          </p>
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translator.loading}
            aria-label={t(
              showTranslated ? 'news.article.aiSummaryShowOriginal' : 'news.article.aiSummaryTranslate',
            )}
            title={t(
              showTranslated ? 'news.article.aiSummaryShowOriginal' : 'news.article.aiSummaryTranslate',
            )}
            aria-pressed={showTranslated}
            className={cn(
              'p-1 rounded-md hover:bg-accent transition-colors',
              showTranslated ? 'text-app-primary-500' : 'text-muted-foreground hover:text-app-primary-500',
              translator.loading && 'opacity-60',
            )}
          >
            {translator.loading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Languages className="w-3.5 h-3.5" />}
          </button>
        </div>
        <p
          className="text-sm text-foreground whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere]"
          dir={displayedDir}
        >
          {displayed}
        </p>
        {translator.error && (
          <p
            className="text-[10px] font-mono text-muted-foreground break-all mt-1"
            dir="ltr"
          >
            {translator.error}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function fmtDate(iso, locale) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-CA', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch {
    return iso
  }
}

// Rendered as a modal child of NewsPage — the list stays mounted
// underneath, no remount, no image-decode flash on close. Props
// replace the old useParams / useLocation / useNavigate plumbing:
// `articleId` is the row to fetch, `onClose` collapses the modal
// (and updates browser history in the parent), and `onOpenArticle`
// lets similar-article hops route back through the parent's modal
// state instead of triggering a fresh route navigation.
export function NewsArticlePage({ articleId, onClose, onOpenArticle }) {
  const { t, i18n } = useTranslation()
  const id = articleId
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  // Image lightbox state — { images: string[], index: number }. The
  // hero image, gallery carousel and any other clickable images on
  // the page open into the same ImageLightbox by setting this.
  const [lightbox, setLightbox] = useState(null)

  const { article, loading, error } = useNewsArticle(id)
  // Owner gate kept for the per-article drill (EnglishArticleDrill
  // below); selection-driven vocab capture is now handled by the
  // global EnglishSelectionAction mounted in App.jsx, so the toolbar
  // GraduationCap button + the article-local sheet have been removed
  // — they were redundant with the global pill.
  const { user: authUser } = useAuth()
  const englishOwner = hasOwnerAccess(authUser)
  // Social posts (kind != 'news') aren't articles — their "body" is
  // already in `summary`, and trying to extract a full article from
  // their permalink either 403s (Reddit) or returns a JS-heavy page
  // with no readable body (YouTube watch pages, Mastodon/Bluesky
  // post permalinks). Gate the content fetcher on this so we don't
  // surface a misleading "couldn't isolate the article body" error.
  const isSocial = !!article && article.source?.kind && article.source.kind !== 'news'
  const youtubeId = isSocial && article?.source?.kind === 'youtube'
    ? extractYouTubeId(article.link)
    : null
  const fullContent = useArticleContent(isSocial ? null : article)
  const savedHook = useSavedArticle(id)
  const mutedHook = useMutedSources()
  const sourceMuted = article?.source?.id ? mutedHook.mutedIds.has(article.source.id) : false
  // Similar articles via the news_similar_articles RPC. Fires once
  // the article id is known; idle until then.
  const similarHook = useSimilarArticles(id, 4)
  // TTS — single-instance per page mount; the hook cancels in-flight
  // audio on unmount so route-changes don't leak speech.
  const tts = useTextToSpeech()
  // Scroll-position memory. `ready` flips true once the body fetcher
  // has either filled content or hit a state where it won't (social
  // posts, paywalled errors). The hook waits on that before trying
  // to restore so we don't land at a too-low scrollTop.
  const isContentSettled = !!article && (
    isSocial
    || !!fullContent.content
    || !!fullContent.error
    || !fullContent.loading
  )
  const { rootRef: scrollRootRef } = useArticleScrollPosition(id, isContentSettled)

  // Reading-progress bar. Implemented as a ref-driven side effect
  // instead of setState so scrolling never re-renders the article
  // body — ReactMarkdown otherwise re-parses on every state change,
  // and downstream <img> elements re-mount + flicker during fast
  // scroll. The progress bar reads scrollTop on each frame and
  // writes the scale directly to its own DOM node.
  const progressBarRef = useRef(null)
  useEffect(() => {
    const el = scrollRootRef.current
    if (!el) return undefined
    let scroller = el.parentElement
    while (scroller && scroller !== document.body) {
      const s = window.getComputedStyle(scroller)
      if (s.overflowY === 'auto' || s.overflowY === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller || scroller === document.body) return undefined
    let raf = 0
    const update = () => {
      const max = scroller.scrollHeight - scroller.clientHeight
      const ratio = max <= 0 ? 0 : Math.max(0, Math.min(1, scroller.scrollTop / max))
      const bar = progressBarRef.current
      if (bar) bar.style.transform = `scaleX(${ratio})`
    }
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(update)
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    update()
    return () => {
      cancelAnimationFrame(raf)
      scroller.removeEventListener('scroll', onScroll)
    }
  }, [scrollRootRef, article?.id])
  // Fire-and-forget read tracking. Upserts (user, article) into
  // news_read_articles on mount so the list dims this card on the
  // next visit / refetch.
  useMarkArticleRead(id)

  // News-tab tap while reading an article: scroll the overlay to
  // the top instead of closing it. The bottom-bar handler in
  // App.jsx dispatches 'news:scroll-to-top' for any /phoenix/news*
  // path; here we route it to the overlay's own scroller (the
  // fixed-position div with overflow-y: auto that wraps Outlet).
  useEffect(() => {
    const handler = () => {
      const el = scrollRootRef.current
      if (!el) return
      let scroller = el.parentElement
      while (scroller && scroller !== document.body) {
        const s = window.getComputedStyle(scroller)
        if (s.overflowY === 'auto' || s.overflowY === 'scroll') break
        scroller = scroller.parentElement
      }
      if (scroller && scroller !== document.body) {
        scroller.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    window.addEventListener('news:scroll-to-top', handler)
    return () => window.removeEventListener('news:scroll-to-top', handler)
  }, [scrollRootRef])
  // feedBase + goBack come from the parent NewsPage now (modal mode);
  // similar-article hops route back through onOpenArticle so the
  // parent's modal state and history stack stay consistent rather
  // than spawning a fresh navigation.
  const goBack = onClose

  // Translation hook needs the fetched body so it doesn't fire
  // before the content cache is populated. Synthesize an article
  // that overlays the freshly-fetched markdown; for social posts —
  // where full_content is never populated — fall back to summary
  // (the actual post text) so the translate button still works.
  const articleForTranslation = article
    ? {
        ...article,
        full_content: fullContent.content
          || article.full_content
          || (isSocial ? article.summary : null),
      }
    : null
  const translationHook = useArticleTranslation(articleForTranslation, locale)

  // Default to translated view when one is needed (UI lang differs
  // from article lang). The toggle lets the user flip back to the
  // original — handy for proper-nouns / quotes that read oddly when
  // transliterated.
  const [showOriginal, setShowOriginal] = useState(false)
  const useTranslated = !!(translationHook.translation && !showOriginal && translationHook.needed)
  const displayTitle = useTranslated ? translationHook.translation.title : article?.title
  const displayBody = useTranslated ? translationHook.translation.content : fullContent.content
  const displayLang = useTranslated ? locale : article?.language

  // Compose the speakable text once article + content are settled.
  // For news with a fetched body: title + body. For social posts:
  // title + summary. The TTS hook handles the markdown strip itself
  // before sending to the synthesiser.
  const speakableText = useMemo(() => {
    if (!article) return ''
    const parts = [article.title]
    if (isSocial && article.summary) parts.push(article.summary)
    else if (displayBody) parts.push(displayBody)
    else if (article.summary) parts.push(article.summary)
    return stripMarkdownForTts(parts.join('\n\n'))
  }, [article, isSocial, displayBody])
  // Per-article direction. The source-level language is sometimes
  // wrong (r/saudiarabia is mixed AR/EN; r/Egypt likewise), so we
  // check the actual title characters and override per item. The
  // body inherits from the title — we don't compute separately to
  // keep header + body aligned.
  const titleForDir = useTranslated ? translationHook.translation.title : article?.title
  const articleDir = isArabicText(titleForDir) ? 'rtl' : 'ltr'
  // Reading time: estimated from the speakable text (already
  // markdown-stripped). 200 wpm reads as a reasonable average for
  // both English and Arabic.
  const readingMinutes = estimateReadingMinutes(speakableText)
  const ttsLang = useTranslated ? locale : (isArabicText(article?.title) ? 'ar' : 'en')
  const handleListen = () => {
    if (!tts.supported || !speakableText) return
    if (tts.playing && !tts.paused) tts.pause()
    else if (tts.paused) tts.resume()
    else tts.play(speakableText, ttsLang)
  }

  return (
    <div ref={scrollRootRef} className="space-y-3 content-fade pb-8">
      {/* Reading progress bar. The inner div's scaleX is written
          directly via ref in the effect above — no React state, no
          re-render of the article body during scroll. */}
      <div
        aria-hidden="true"
        className="fixed top-0 inset-x-0 h-0.5 z-50 pointer-events-none"
      >
        <div
          ref={progressBarRef}
          className="h-full bg-app-primary-500 origin-start"
          style={{ transform: 'scaleX(0)' }}
        />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5" dir="auto">
            <Newspaper className="w-4 h-4 text-app-primary-500" />
            {t('news.title')}
          </h1>
        </div>
        {article && (
          <button
            type="button"
            onClick={savedHook.toggle}
            disabled={savedHook.pending || !savedHook.loaded}
            aria-label={t(savedHook.saved ? 'news.article.unsave' : 'news.article.save')}
            aria-pressed={savedHook.saved}
            className={
              savedHook.saved
                ? 'p-1.5 rounded-md text-app-primary-500 hover:bg-accent transition-colors disabled:opacity-50'
                : 'p-1.5 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent transition-colors disabled:opacity-50'
            }
          >
            <Bookmark className={savedHook.saved ? 'w-4 h-4 fill-current' : 'w-4 h-4'} />
          </button>
        )}
        {article?.source?.id && (
          <button
            type="button"
            onClick={() => mutedHook.toggle(article.source.id)}
            aria-label={t(sourceMuted ? 'news.article.unmuteSource' : 'news.article.muteSource', { source: article.source?.name || '' })}
            aria-pressed={sourceMuted}
            className={cn(
              'p-1.5 rounded-md hover:bg-accent transition-colors',
              sourceMuted ? 'text-app-primary-500' : 'text-muted-foreground hover:text-app-primary-500',
            )}
          >
            {sourceMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}
        {article && tts.supported && speakableText && (
          <>
            <button
              type="button"
              onClick={handleListen}
              aria-label={t(
                tts.playing && !tts.paused
                  ? 'news.article.ttsPause'
                  : tts.paused
                    ? 'news.article.ttsResume'
                    : 'news.article.ttsPlay',
              )}
              aria-pressed={tts.playing}
              className={cn(
                'p-1.5 rounded-md hover:bg-accent transition-colors',
                tts.playing ? 'text-app-primary-500' : 'text-muted-foreground hover:text-app-primary-500',
              )}
            >
              {tts.playing && !tts.paused ? (
                <Pause className="w-4 h-4" />
              ) : tts.paused ? (
                <Play className="w-4 h-4" />
              ) : (
                <Headphones className="w-4 h-4" />
              )}
            </button>
            {(tts.playing || tts.paused) && (
              <button
                type="button"
                onClick={tts.stop}
                aria-label={t('news.article.ttsStop')}
                className="p-1.5 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent transition-colors"
              >
                <Square className="w-4 h-4" />
              </button>
            )}
          </>
        )}
        {/* Re-fetch button only makes sense for news articles where
            we extract a body — there's nothing to refresh for a
            social post since the body is already in the row. */}
        {!isSocial && (fullContent.content || fullContent.error) && (
          <button
            type="button"
            onClick={() => fullContent.run({ force: true })}
            disabled={fullContent.loading}
            aria-label={t('news.article.refetch')}
            className="p-1.5 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent transition-colors"
          >
            <RefreshCcw className={fullContent.loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-3 text-xs text-red-600" dir="auto">{error}</CardContent>
        </Card>
      ) : !article ? (
        <EmptyState icon={Globe} title={t('news.article.notFound')} />
      ) : (
        <article className="space-y-4">
          {/* Hero media. Three render paths in order of specificity:
              1) Reddit gallery (multi-image) → swipeable carousel
              2) Reddit video (v.redd.it MP4) → muted inline player
              3) Anything else with image_url → original single hero
              fallback to the plain image when none of the rich shapes
              are populated. */}
          {article.media_kind === 'gallery' && Array.isArray(article.media_images) && article.media_images.length > 0 ? (
            <div className="w-full rounded-lg overflow-hidden">
              <GalleryCarousel
                images={article.media_images}
                title={article.title}
                hero
                onImageClick={(idx) => setLightbox({ images: article.media_images, index: idx })}
              />
            </div>
          ) : article.media_kind === 'video' && article.media_video_url ? (
            <div className="w-full rounded-lg overflow-hidden">
              <VideoBlock
                videoUrl={article.media_video_url}
                thumbnail={article.image_url}
                title={article.title}
                t={t}
                hero
              />
            </div>
          ) : article.image_url && (
            <button
              type="button"
              onClick={() => setLightbox({ images: [article.image_url], index: 0 })}
              className="aspect-video w-full rounded-lg overflow-hidden bg-muted block"
              aria-label={t('news.article.viewImage', 'View image')}
            >
              <img
                src={(article.image_url || '').replace(/&amp;/g, '&')}
                alt=""
                loading="eager"
                decoding="async"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const wrap = e.currentTarget.parentElement
                  if (wrap) wrap.style.display = 'none'
                }}
                className="w-full h-full object-cover"
              />
            </button>
          )}
          <header className="space-y-2">
            <h2
              className="text-2xl font-bold text-foreground leading-tight"
              dir={articleDir}
            >
              {displayTitle}
            </h2>
            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap" dir="auto">
              {article.source?.name && (
                <span className="font-medium text-foreground">{article.source.name}</span>
              )}
              {article.published_at && (
                <>
                  <span>·</span>
                  <span className="font-mono" dir="ltr">{fmtDate(article.published_at, locale)}</span>
                </>
              )}
              {readingMinutes > 0 && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-0.5" dir="ltr">
                    <Clock className="w-3 h-3" />
                    {t('news.article.readingMinutes', { count: readingMinutes })}
                  </span>
                </>
              )}
              {article.category && (
                <>
                  <span>·</span>
                  <span className="bg-muted px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide">
                    {t(`news.category.${article.category}`, { defaultValue: article.category })}
                  </span>
                </>
              )}
            </div>
            {Array.isArray(article.tags) && article.tags.length > 0 && (
              <div className="flex gap-1.5 flex-wrap pt-1" dir="auto">
                {article.tags.map((tag) => (
                  <DetailTagChip key={tag} tag={tag} />
                ))}
              </div>
            )}
          </header>

          {article.ai_summary && (
            <AiSummaryCard
              summary={article.ai_summary}
              articleLang={article.language}
              locale={locale}
              t={t}
            />
          )}

          {/* Translation panel. Manual — fires only when the user
              taps "Translate" so we don't burn Gemini tokens on
              articles they never asked us to translate. Now enabled
              for social posts too — translate-article falls back to
              article.summary as the source body when full_content
              isn't there. */}
          {translationHook.needed && !translationHook.translation && !translationHook.loading && !translationHook.error && (
            <button
              type="button"
              onClick={() => translationHook.run()}
              className="w-full flex items-center justify-center gap-1.5 text-xs rounded-md border border-app-primary-500/30 bg-app-primary-500/5 hover:bg-app-primary-500/10 px-2.5 py-2 text-app-primary-500 font-medium transition-colors"
              dir="auto"
            >
              <Languages className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{t('news.article.translateAction')}</span>
            </button>
          )}

          {translationHook.needed && (translationHook.loading || translationHook.translation || translationHook.error) && (
            <div
              className="flex flex-col gap-1 text-[11px] rounded-md border border-app-primary-500/30 bg-app-primary-500/5 px-2.5 py-1.5"
              dir="auto"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-app-primary-500 min-w-0">
                  {translationHook.loading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                  ) : (
                    <Languages className="w-3.5 h-3.5 flex-shrink-0" />
                  )}
                  <span className="truncate">
                    {translationHook.loading
                      ? t('news.article.translating')
                      : translationHook.error
                        ? t('news.article.translationFailed')
                        : t('news.article.translatedAuto')}
                  </span>
                </div>
                {translationHook.translation && !translationHook.loading && (
                  <button
                    type="button"
                    onClick={() => setShowOriginal((v) => !v)}
                    className="text-app-primary-500 hover:underline whitespace-nowrap font-medium"
                  >
                    {showOriginal
                      ? t('news.article.showTranslation')
                      : t('news.article.showOriginal')}
                  </button>
                )}
                {translationHook.error && !translationHook.translation && (
                  <button
                    type="button"
                    onClick={() => translationHook.run({ force: true })}
                    className="text-app-primary-500 hover:underline whitespace-nowrap font-medium"
                  >
                    {t('news.article.retry')}
                  </button>
                )}
              </div>
              {/* Surface the actual error detail under the title so
                  failures are debuggable from the user's screen rather
                  than buried in console / function logs. Monospaced
                  small text so a long backend detail wraps cleanly. */}
              {translationHook.error && !translationHook.translation && (
                <p
                  className="text-[10px] font-mono text-muted-foreground break-all leading-tight"
                  dir="ltr"
                >
                  {translationHook.error}
                </p>
              )}
            </div>
          )}

          {/* Social-post body. Rendered directly from article.summary
              (which is the full post text, capped at 2k chars). Skip
              the loading skeleton, the "extract failed" warning, and
              the news content section entirely. YouTube gets an
              embedded player above the text. */}
          {isSocial && (
            <Card>
              <CardContent className="p-3 sm:p-5 space-y-3">
                {youtubeId && (
                  <div className="aspect-video w-full rounded-md overflow-hidden bg-muted">
                    <iframe
                      title={article.title}
                      className="w-full h-full"
                      src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                )}
                {(() => {
                  // Use translated text when the user has opted in;
                  // otherwise the raw summary (i.e. the post text).
                  const body = useTranslated
                    ? translationHook.translation.content
                    : article.summary
                  if (!body) {
                    return (
                      <p className="text-xs text-muted-foreground" dir="auto">
                        {t('news.article.noSocialBody')}
                      </p>
                    )
                  }
                  return (
                    <p
                      className="text-sm text-foreground whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere]"
                      dir={isArabicText(body) ? 'rtl' : 'ltr'}
                    >
                      {body}
                    </p>
                  )
                })()}
              </CardContent>
            </Card>
          )}

          {/* Full body. Auto-fetches on first open and caches in
              news_articles.full_content. When a translation is
              active, the markdown swap-in is synchronous since the
              cache lives in article.translations. */}
          {!isSocial && fullContent.loading && !fullContent.content && (
            <Card>
              <CardContent className="p-3 sm:p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground" dir="auto">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('news.article.loadingContent')}
                </div>
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-11/12" />
                <Skeleton className="h-3 w-10/12" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-9/12" />
              </CardContent>
            </Card>
          )}

          {!isSocial && displayBody && (
            <Card>
              <CardContent className="p-3 sm:p-5">
                <ArticleBody markdown={displayBody} dir={articleDir} />
              </CardContent>
            </Card>
          )}

          {!isSocial && fullContent.error && !fullContent.content && (
            <Card>
              <CardContent className="p-3 sm:p-4 space-y-2">
                <p className="text-xs text-amber-700 dark:text-amber-400" dir="auto">
                  {t('news.article.extractFailed')}
                </p>
                <p className="text-[11px] text-muted-foreground font-mono break-all" dir="ltr">
                  {fullContent.error}
                </p>
                {article.summary && (
                  <div className="pt-2 border-t border-border/40">
                    <p className="text-[10px] text-muted-foreground mb-1" dir="auto">
                      {t('news.article.fallbackExcerpt')}
                    </p>
                    <p
                      className="text-sm text-foreground whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere]"
                      dir={isArabicText(article.summary) ? 'rtl' : 'ltr'}
                    >
                      {article.summary}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <a
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button variant="outline" className="w-full" size="lg">
              <ExternalLink className="w-4 h-4 me-2 rtl:-scale-x-100" />
              {t('news.article.readFullOnSource', {
                source: article.source?.name || t('news.article.source'),
              })}
            </Button>
          </a>

          {/* Per-article AI chat. Quota-conscious — collapsed CTA
              by default, no LLM calls until the user submits a
              question. The article body is injected as system
              context on the server. */}
          <ArticleChat articleId={article.id} />

          {/* Reading-comprehension drill (owner-only). LLM-generated,
              cached per article in english_article_drills so re-opens
              are free. Skipped for social posts where the body is too
              thin to quiz on. */}
          {englishOwner && !isSocial && (fullContent.content || article.summary) && (
            <EnglishArticleDrill articleId={article.id} />
          )}

          {/* Reddit comments overlay. Auto-loads on mount for Reddit
              articles only; renders nothing for other sources. The
              article id is the key into news_reddit_comments cache —
              passing it lets a re-open serve from the 30-min cache. */}
          {article.source?.kind === 'reddit' && (
            <RedditCommentsList articleLink={article.link} articleId={article.id} />
          )}

          {/* Similar articles. Hidden when the RPC returns nothing
              (very specific titles, or post that share no significant
              tokens with others). Cards are compact — title + source
              + time — and link to the same /phoenix/news/:id route. */}
          {similarHook.articles.length > 0 && (
            <div className="space-y-2 pt-2">
              <p
                className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1"
                dir="auto"
              >
                {t('news.article.similarTitle')}
              </p>
              <div className="space-y-2">
                {similarHook.articles.map((a) => (
                  // replace=true so article-hops via "similar articles"
                  // don't deepen the history stack. After 3 hops, an
                  // iOS swipe-back from the latest article would
                  // article. Routed through onOpenArticle so the
                  // parent NewsPage swaps its modal state without
                  // a fresh navigation that would tear the list down.
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => onOpenArticle?.(a.id)}
                    className="block w-full text-start"
                  >
                    <Card className="hover:border-app-primary-500/40 transition-colors overflow-hidden">
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          {a.image_url && (
                            <img
                              src={a.image_url}
                              alt=""
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = 'none' }}
                              className="w-16 h-16 rounded-md object-cover flex-shrink-0 bg-muted"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-sm font-medium text-foreground line-clamp-2"
                              dir={isArabicText(a.title) ? 'rtl' : 'ltr'}
                            >
                              {a.title}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1 text-[10px] text-muted-foreground" dir="auto">
                              {a.source_name && <span>{a.source_name}</span>}
                              {a.published_at && (
                                <>
                                  <span>·</span>
                                  <span dir="ltr" className="font-mono">{fmtDate(a.published_at, locale)}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                ))}
              </div>
            </div>
          )}
        </article>
      )}
      <ImageLightbox state={lightbox} onClose={() => setLightbox(null)} />
    </div>
  )
}
